<template>
  <v-card outlined class="questions-card pa-3">
    <template v-if="hasAssumptions">
      <div class="caption text-uppercase text--secondary mb-2">Hypothèses</div>
      <div class="align-center mb-3" style="gap: 6px">

        <div v-for="(a, i) in visibleAssumptions" :key="i" style="display: contents">
          <v-text-field
            v-if="editingAssumptionIdx === i"
            v-model="assumptionDrafts[i]"
            outlined
            autofocus
            hide-details
            class="assumption-field d-block"
            @blur="saveAssumption(i)"
            @keyup.enter.native="saveAssumption(i)"
            @keyup.esc.native="cancelAssumption(i)"
          />
          <v-chip
            v-else
            small
            outlined
            class="editable-chip d-block"
            @click="startEditAssumption(i)"
          >
            <v-icon x-small left>mdi-pencil</v-icon>
            {{ a }}
          </v-chip>
        </div>

        <v-chip
          v-if="!assumptionsExpanded && assumptions.length > 3"
          small
          outlined
          class="expand-chip d-block"
          @click="assumptionsExpanded = true"
        >
          <v-icon x-small left>mdi-plus</v-icon>
          {{ assumptions.length - 3 }} de plus
        </v-chip>

        <span v-for="q in assumedQuestions" :key="q.key" style="display: contents">
          <v-chip
            small
            outlined
            class="editable-chip"
            @click="startEditAssumed(q.key)"
          >
            <v-icon x-small left>mdi-pencil</v-icon>
            {{ q.label }}
          </v-chip>
          <v-text-field
            v-if="editingAssumedKey === q.key"
            v-model="assumedDrafts[q.key]"
            :suffix="q.unit || ''"
            dense
            outlined
            autofocus
            hide-details
            class="assumption-field"
            @blur="saveAssumed(q)"
            @keyup.enter.native="saveAssumed(q)"
            @keyup.esc.native="cancelAssumed(q)"
          />
        </span>

      </div>
    </template>

    <template v-if="sortedQuestions.length">
      <div class="caption text-uppercase text--secondary mb-2">Questions</div>

      <v-window v-model="carouselIndex">
        <v-window-item v-for="q in sortedQuestions" :key="q.key">
          <div class="carousel-slide">
            <v-text-field
              v-if="q.type === 'float' || q.type === 'int'"
              v-model="localAnswers[q.key]"
              :label="q.label"
              :suffix="q.unit || ''"
              :step="q.type === 'int' ? 1 : 'any'"
              :min="q.min != null ? q.min : undefined"
              :max="q.max != null ? q.max : undefined"
              type="number"
              dense
              outlined
              hide-details="auto"
              @input="emitAnswers"
              @keyup.enter.native="advance"
            />
            <div v-else-if="q.type === 'select'">
              <div class="caption mb-2 text-h6">{{ q.label }}</div>
              <div class="option-btn-group">
                <v-btn
                  v-for="opt in q.options"
                  :key="opt"
                  :color="localAnswers[q.key] === opt ? 'primary' : ''"
                  :outlined="localAnswers[q.key] !== opt"
                  depressed
                  small
                  class="option-btn"
                  @click="localAnswers[q.key] = opt; emitAnswers(); advanceDelayed()"
                >{{ opt }}</v-btn>
              </div>
            </div>
            <v-switch
              v-else-if="q.type === 'boolean'"
              v-model="localAnswers[q.key]"
              :label="q.label"
              true-value="true"
              false-value="false"
              dense
              hide-details="auto"
              @change="emitAnswers(); advanceDelayed()"
            />
          </div>
        </v-window-item>
      </v-window>

      <div class="carousel-nav mt-2">
        <v-btn icon x-small :disabled="carouselIndex === 0" @click="carouselIndex--">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <span class="caption text--secondary mx-1">{{ carouselIndex + 1 }} / {{ sortedQuestions.length }}</span>
        <v-btn icon x-small :disabled="carouselIndex === sortedQuestions.length - 1" @click="carouselIndex++">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </template>
  </v-card>
</template>

<script>
export default {
  name: 'QuestionsCard',
  props: {
    questions: { type: Array, required: true },
    value: { type: Object, default: () => ({}) },
    assumedQuestions: { type: Array, default: () => [] },
    assumptions: { type: Array, default: () => [] },
  },
  data() {
    const assumptionDrafts = {}
    this.assumptions.forEach((a, i) => { assumptionDrafts[i] = a })

    const assumedDrafts = {}
    this.assumedQuestions.forEach(q => { assumedDrafts[q.key] = q.default_value ?? '' })

    return {
      localAnswers: { ...(this.value || {}) },
      editingAssumptionIdx: null,
      editingAssumedKey: null,
      assumptionDrafts,
      assumedDrafts,
      assumptionsExpanded: false,
      carouselIndex: 0,
    }
  },
  computed: {
    hasAssumptions() {
      return this.assumptions.length > 0 || this.assumedQuestions.length > 0
    },
    visibleAssumptions() {
      return this.assumptionsExpanded ? this.assumptions : this.assumptions.slice(0, 3)
    },
    sortedQuestions() {
      return [...this.questions].sort((a, b) => (b.importance ?? 5) - (a.importance ?? 5))
    },
  },
  methods: {
    startEditAssumption(i) {
      this.$set(this.assumptionDrafts, i, this.assumptions[i])
      this.editingAssumptionIdx = i
    },
    saveAssumption(i) {
      this.$emit('update:assumption', { index: i, value: this.assumptionDrafts[i] })
      this.editingAssumptionIdx = null
    },
    cancelAssumption(i) {
      this.$set(this.assumptionDrafts, i, this.assumptions[i])
      this.editingAssumptionIdx = null
    },
    startEditAssumed(key) {
      const q = this.assumedQuestions.find(q => q.key === key)
      if (q) this.$set(this.assumedDrafts, key, q.default_value ?? '')
      this.editingAssumedKey = key
    },
    saveAssumed(q) {
      this.$emit('update:assumed', { key: q.key, default_value: this.assumedDrafts[q.key] })
      this.editingAssumedKey = null
    },
    cancelAssumed(q) {
      this.$set(this.assumedDrafts, q.key, q.default_value ?? '')
      this.editingAssumedKey = null
    },
    emitAnswers() {
      this.$emit('input', { ...this.localAnswers })
    },
    advance() {
      if (this.carouselIndex < this.sortedQuestions.length - 1) this.carouselIndex++
    },
    advanceDelayed() {
      setTimeout(this.advance, 200)
    },
  },
}
</script>

<style scoped>
.questions-card {
  background: var(--app-card-bg, #fff);
}
.editable-chip {
  cursor: pointer;
}
.expand-chip {
  cursor: pointer;
  opacity: 0.7;
}
.assumption-field {
}
.carousel-slide {
  min-height: 80px;
  padding: 4px 2px;
}
.carousel-nav {
  display: flex;
  align-items: center;
  justify-content: center;
}
.option-btn-group {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.option-btn {
  text-transform: none;
  letter-spacing: 0;
  font-size: 0.875rem;
  padding: 0 16px !important;
}
</style>
